import axios from 'axios';
import Cookies from 'js-cookie';
import type { TFunction } from 'i18next';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import type { Location, NavigateFunction } from 'react-router-dom';
import Swal from 'sweetalert2';

import ApiKey from 'Components/api/ApiKey';
import BaseApi from 'Components/api/BaseApi';
import { loadCreateResumeProfileAccess } from 'candidate/core/guards/create-resume-profile-access';
import type { CreateResumeProfileField } from 'candidate/core/guards/create-resume-profile-access';
import type {
   CandidateSidebarAction,
   CandidateSidebarItemClickData,
   CandidateSidebarSection,
} from 'candidate/candidate-sidebar/candidate-sidebar.types';
import type { RootStore } from 'core/stores/root-store';

type CandidateRouteState = {
   missingCreateResumeProfileFields?: CreateResumeProfileField[];
   showCreateResumeProfileLockDialog?: boolean;
   showCreateResumeProfileValidation?: boolean;
};

export class CandidateModuleStore {
   checkingSidebarItemId: string | null = null;
   missingResumeProfileFields: CreateResumeProfileField[] = [];
   resumeLockDialogOpen = false;

   constructor(
      private readonly rootStore: RootStore,
      private readonly navigate: NavigateFunction,
      private readonly t: TFunction<'global'>
   ) {
      makeObservable(this, {
         checkingSidebarItemId: observable,
         missingResumeProfileFields: observable,
         resumeLockDialogOpen: observable,
         sidebarSections: computed,
         setCheckingSidebarItemId: action.bound,
         setMissingResumeProfileFields: action.bound,
         setResumeLockDialogOpen: action.bound,
         openCreateResumeProfileLockDialog: action.bound,
         handleResumeLockRouteState: action.bound,
         handleSidebarAction: action.bound,
         handleCreateResumeCanActivate: action.bound,
      });
   }

   get sidebarSections(): CandidateSidebarSection[] {
      return [
         {
            id: 'searchWork',
            hideHeader: true,
            wrapperClassName: 'SidebarSectionTop',
            desktopBodyClassName: 'SBBody',
            items: [
               {
                  id: 'search-job',
                  type: 'link',
                  to: '/searchjob',
                  itemClassName: 'SidebarChangeLogo',
                  iconClassName: 'SidebarChangeLogoBg',
                  iconBackgroundSrc: '/Images/employerSide/bg10.png',
                  iconSrc: '/Images/jobseekerSide/search-icon.png',
                  titleKey: 'jobseekerSidebar.searchJobs',
               },
            ],
         },
         {
            id: 'myProfile',
            titleKey: 'jobseekerSidebar.userMenu',
            wrapperClassName: 'SidebarSection1',
            desktopBodyClassName: 'SBBody',
            loadingItemId: this.checkingSidebarItemId,
            items: [
               {
                  id: 'my-cv-list',
                  type: 'link',
                  to: '/candidate/my-cv-list',
                  itemClassName: 'SidebarCreatJob',
                  iconClassName: 'SidebarCreatJobBg',
                  iconBackgroundSrc: '/Images/employerSide/bg1.png',
                  iconSrc: '/Images/jobseekerSide/cv-list-black.png',
                  titleKey: 'jobseekerSidebar.myCVList',
               },
               {
                  id: 'create-resume',
                  type: 'link',
                  to: '/candidate/resume/create',
                  canActivate: this.handleCreateResumeCanActivate,
                  itemClassName: 'SidebarManageJob',
                  iconClassName: 'SidebarManageJobBg',
                  iconBackgroundSrc: '/Images/employerSide/bg2.png',
                  iconSrc: '/Images/employerSide/icon8color.png',
                  titleKey: 'jobseekerSidebar.createCV',
               },
            ],
         },
         {
            id: 'quickLinks',
            titleKey: 'jobseekerSidebar.quickLinks',
            wrapperClassName: 'SidebarSection2',
            headerClassName: 'mt-3',
            desktopBodyClassName: 'body',
            items: [
               {
                  id: 'manage-alerts',
                  type: 'link',
                  to: '/alerts/index',
                  itemClassName: 'SidebarMyProfile',
                  iconClassName: 'SidebarMyProfileBg',
                  iconBackgroundSrc: '/Images/employerSide/bg7.png',
                  iconSrc: '/Images/jobseekerSide/Manage-Alerts.png',
                  titleKey: 'jobseekerSidebar.manageAlerts',
               },
               {
                  id: 'saved-jobs',
                  type: 'link',
                  to: '/candidate/saved-jobs',
                  itemClassName: 'SidebarFavouriteJob',
                  iconClassName: 'SidebarFavouriteJobBg',
                  iconBackgroundSrc: '/Images/employerSide/bg4.png',
                  iconSrc: '/Images/employerSide/icon4color.png',
                  titleKey: 'jobseekerSidebar.savedJobs',
               },
               {
                  id: 'applied-jobs',
                  type: 'link',
                  to: '/candidate/applied-jobs',
                  itemClassName: 'SidebarChangePass',
                  iconClassName: 'SidebarChangePassBg',
                  iconBackgroundSrc: '/Images/employerSide/bg9.png',
                  iconSrc: '/Images/jobseekerSide/Applied-Jobs-black.png',
                  titleKey: 'jobseekerSidebar.appliedJobs',
               },
               {
                  id: 'mailing-history',
                  type: 'link',
                  to: '/candidate/mailing-history',
                  itemClassName: 'SidebarMailHistory',
                  iconClassName: 'SidebarMailHistoryBg',
                  iconBackgroundSrc: '/Images/employerSide/bg6.png',
                  iconSrc: '/Images/employerSide/icon6color.png',
                  titleKey: 'jobseekerSidebar.mailHistory',
               },
            ],
         },
         {
            id: 'settings',
            titleKey: 'jobseekerSidebar.myProfile',
            titleClassName: 'text-black',
            wrapperClassName: 'SidebarSection3',
            headerClassName: 'mt-3',
            desktopBodyClassName: 'body',
            items: [
               {
                  id: 'profile',
                  type: 'link',
                  to: '/candidate/profile',
                  itemClassName: 'SidebarEditProfile',
                  iconClassName: 'SidebarEditProfileBg',
                  iconBackgroundSrc: '/Images/employerSide/bg8.png',
                  iconSrc: '/Images/jobseekerSide/profile-icon.png',
                  titleKey: 'jobseekerSidebar.profileMenu',
               },
               {
                  id: 'change-password',
                  type: 'link',
                  to: '/candidate/change-password',
                  itemClassName: 'SidebarChangePass',
                  iconClassName: 'SidebarChangePassBg',
                  iconBackgroundSrc: '/Images/employerSide/bg9.png',
                  iconSrc: '/Images/employerSide/icon9color.png',
                  titleKey: 'jobseekerSidebar.changePassword',
               },
               {
                  id: 'logout',
                  type: 'action',
                  action: 'logout',
                  itemClassName: 'SidebarImportJob',
                  iconClassName: 'SidebarImportJobBg',
                  iconBackgroundSrc: '/Images/employerSide/bg5.png',
                  iconSrc: '/Images/employerSide/icon11color.png',
                  titleKey: 'jobseekerSidebar.logOut',
               },
            ],
         },
      ];
   }

   setCheckingSidebarItemId(itemId: string | null): void {
      this.checkingSidebarItemId = itemId;
   }

   setMissingResumeProfileFields(fields: CreateResumeProfileField[]): void {
      this.missingResumeProfileFields = fields;
   }

   setResumeLockDialogOpen(open: boolean): void {
      this.resumeLockDialogOpen = open;
   }

   openCreateResumeProfileLockDialog(fields: CreateResumeProfileField[] = []): void {
      this.setMissingResumeProfileFields(fields);
      this.resumeLockDialogOpen = true;
   }

   handleResumeLockRouteState(location: Location): void {
      const state = location.state as CandidateRouteState | null;

      if (!state?.showCreateResumeProfileLockDialog) {
         return;
      }

      const restState = { ...state };
      delete restState.showCreateResumeProfileLockDialog;

      this.openCreateResumeProfileLockDialog(state.missingCreateResumeProfileFields ?? []);
      this.navigate(
         {
            hash: location.hash,
            pathname: location.pathname,
            search: location.search,
         },
         {
            replace: true,
            state: Object.keys(restState).length > 0 ? restState : null,
         }
      );
   }

   handleSidebarAction(actionType: CandidateSidebarAction): void {
      if (actionType === 'logout') {
         this.handleLogout();
      }
   }

   async handleCreateResumeCanActivate({ event, item }: CandidateSidebarItemClickData): Promise<void> {
      if (item.type !== 'link') {
         return;
      }

      event.preventDefault();

      if (this.checkingSidebarItemId) {
         return;
      }

      this.setCheckingSidebarItemId(item.id);

      try {
         const access = await loadCreateResumeProfileAccess();

         if (access.user) {
            this.rootStore.userStore.setUser(access.user);
         }

         if (!access.allowed) {
            this.navigateToProfileWithLockDialog(access.missingFields);
            return;
         }

         this.navigate(item.to, {
            state: {
               createResumeProfileAccess: 'allowed',
            },
         });
      } catch {
         this.navigateToProfileWithLockDialog();
      } finally {
         runInAction(() => {
            this.checkingSidebarItemId = null;
         });
      }
   }

   private navigateToProfileWithLockDialog(fields: CreateResumeProfileField[] = []): void {
      this.navigate('/candidate/profile', {
         state: {
            missingCreateResumeProfileFields: fields,
            showCreateResumeProfileLockDialog: true,
            showCreateResumeProfileValidation: true,
         },
      });
   }

   private async handleLogout(): Promise<void> {
      try {
         const confirmationResult = await Swal.fire({
            icon: 'question',
            showCancelButton: true,
            text: this.t('jobseekerSidebar.confirmTxt'),
            title: this.t('jobseekerSidebar.confirmTitle'),
            cancelButtonText: this.t('jobseekerSidebar.no'),
            confirmButtonText: this.t('jobseekerSidebar.yes'),
         });

         if (!confirmationResult.isConfirmed) {
            return;
         }

         await axios.post(`${BaseApi}/users/logout`, null, {
            headers: {
               key: ApiKey,
               token: Cookies.get('tokenClient'),
               'Content-Type': 'application/json',
            },
         });

         Cookies.remove('fname');
         Cookies.remove('user_id');
         Cookies.remove('user_type');
         Cookies.remove('tokenClient');

         this.navigate('/');
         window.location.reload();

         const toast = Swal.mixin({
            timer: 3000,
            toast: true,
            position: 'top-end',
            timerProgressBar: true,
            showConfirmButton: false,
            didOpen: (toastElement) => {
               toastElement.addEventListener('mouseenter', Swal.stopTimer);
               toastElement.addEventListener('mouseleave', Swal.resumeTimer);
            },
         });

         toast.fire({
            icon: 'success',
            title: this.t('jobseekerSidebar.successTitle'),
         });
      } catch {
         Swal.fire({
            icon: 'error',
            title: this.t('jobseekerSidebar.failedTitle'),
            confirmButtonText: this.t('jobseekerSidebar.close'),
         });
      }
   }
}
